import React, { useEffect, useState } from "react";
import { Camera, FileText, Loader2, MapPin, Plus, Trash2, X } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../services/api";
import { activeCurrencySymbol } from "../utils/currency";
import { getTokenInfo } from "../utils/auth";
import { MAX_PDF_PAGES, pdfToImages } from "../utils/pdfToImages";

// Uploads over ~1MB are rejected by the server (HTTP 413).
const DIRECT_UPLOAD_LIMIT = 900 * 1024;

const uploadError = (err: any, fallback: string) => {
  const status = err?.response?.status;
  if (err?.response?.data?.error) return err.response.data.error as string;
  if (status === 413) return "This file is too large to upload";
  if (status === 502 || status === 503 || status === 504) return "The server is busy — please try again in a minute";
  return fallback;
};

/** Read one file (photo, or a small PDF) on the server: start a job, poll it. */
const readOne = async (file: File): Promise<ImportCategory[]> => {
  const body = new FormData();
  body.append("image", file);
  const start = await api.post("/menu-import/extract", body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const jobId: string = start.data?.data?.jobId;

  // Reading runs on the server in the background; check every 3s.
  const deadline = Date.now() + 6 * 60 * 1000;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    const poll = await api.get(`/menu-import/extract/${jobId}`);
    const job = poll.data?.data || {};
    if (job.status === "done") return job.categories || [];
    if (job.status === "error") throw new Error(job.error || "Couldn't read the menu");
    if (Date.now() > deadline) throw new Error("Reading the menu is taking too long — please try again");
  }
};

/** Join pages: same-named categories are merged, duplicate dishes dropped. */
const mergeCategories = (pages: ImportCategory[][]): ImportCategory[] => {
  const merged: ImportCategory[] = [];
  for (const page of pages) {
    for (const cat of page) {
      const key = cat.name.trim().toLowerCase();
      const existing = merged.find((c) => c.name.trim().toLowerCase() === key);
      if (!existing) {
        merged.push({ name: cat.name, items: [...cat.items] });
        continue;
      }
      for (const item of cat.items) {
        const dup = existing.items.some((i) => i.name.trim().toLowerCase() === item.name.trim().toLowerCase());
        if (!dup) existing.items.push(item);
      }
    }
  }
  return merged;
};

type Location = { _id: string; hotelName: string };

type ImportItem = {
  name: string;
  price: number; // full plate, or the only price
  halfPrice: number; // 0 = no half plate
  description: string;
  foodType: "veg" | "non-veg" | "both";
};
type ImportCategory = { name: string; items: ImportItem[] };

interface MenuImportModalProps {
  onClose: () => void;
  onImported: () => void;
}

const inputClass =
  "w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

/**
 * Add a whole menu from a photo: AI reads the menu card, the admin checks and
 * fixes the list, then it's saved as categories and items.
 */
const MenuImportModal: React.FC<MenuImportModalProps> = ({ onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState("");
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<ImportCategory[] | null>(null);

  // Which location gets the menu: the only one, or the admin's pick when
  // they have several (defaults to the active location).
  const tokenInfo = getTokenInfo();
  const activeId: string = tokenInfo?.restaurantId || "";
  const [locations, setLocations] = useState<Location[]>([]);
  const [targetId, setTargetId] = useState(activeId);
  useEffect(() => {
    if (tokenInfo?.role !== "location_admin") return;
    api
      .get("/admin/getHotelInformation")
      .then((res) => {
        const list: Location[] = res.data?.data || [];
        setLocations(list);
        if (!list.some((l) => l._id === activeId) && list[0]) setTargetId(list[0]._id);
      })
      .catch(() => setLocations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const targetName = locations.find((l) => l._id === targetId)?.hotelName || "";

  const itemCount = (categories || []).reduce((n, c) => n + c.items.length, 0);
  const symbol = activeCurrencySymbol();

  const onSelect = (selected?: File) => {
    if (!selected) return;
    const isPdf = selected.type === "application/pdf";
    if (!selected.type.startsWith("image/") && !isPdf) {
      toast.error("Please choose a photo or PDF of the menu");
      return;
    }
    if (isPdf && selected.size > 25 * 1024 * 1024) {
      toast.error("PDF is too large (max 25 MB)");
      return;
    }
    setFile(selected);
    // Photos get a preview; a PDF shows its file name.
    setPreviewUrl(isPdf ? "" : URL.createObjectURL(selected));
    setCategories(null);
  };

  const readMenu = async () => {
    if (!file) return;
    setReading(true);
    try {
      // A big PDF is sent page by page as photos (upload size limit);
      // photos and small PDFs go as they are.
      let parts: File[] = [file];
      if (file.type === "application/pdf" && file.size > DIRECT_UPLOAD_LIMIT) {
        setProgress("Preparing PDF pages…");
        parts = await pdfToImages(file);
        if (!parts.length) {
          toast.error("This PDF has no pages");
          return;
        }
      }

      let done = 0;
      setProgress(parts.length > 1 ? `Reading ${parts.length} pages…` : "");
      const results = await Promise.allSettled(
        parts.map((part) =>
          readOne(part).finally(() => {
            done += 1;
            if (parts.length > 1) setProgress(`Read ${done} of ${parts.length} pages…`);
          }),
        ),
      );
      const ok = results.filter((r): r is PromiseFulfilledResult<ImportCategory[]> => r.status === "fulfilled");
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");

      const found = mergeCategories(ok.map((r) => r.value)).filter((c) => c.items.length);
      if (!found.length) {
        toast.error(failed ? uploadError(failed.reason, failed.reason?.message || "Couldn't read the menu") : "No menu items were found in this file");
        return;
      }
      if (failed) toast.error(`Some pages couldn't be read (${results.length - ok.length} of ${results.length}) — check the list`);
      setCategories(found);
    } catch (err: any) {
      toast.error(uploadError(err, err?.message || "Couldn't read the menu"));
    } finally {
      setReading(false);
      setProgress("");
    }
  };

  const updateCategory = (ci: number, name: string) =>
    setCategories((prev) => prev!.map((c, i) => (i === ci ? { ...c, name } : c)));
  const removeCategory = (ci: number) =>
    setCategories((prev) => prev!.filter((_, i) => i !== ci));
  const updateItem = (ci: number, ii: number, patch: Partial<ImportItem>) =>
    setCategories((prev) =>
      prev!.map((c, i) =>
        i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : c,
      ),
    );
  const removeItem = (ci: number, ii: number) =>
    setCategories((prev) =>
      prev!.map((c, i) => (i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c)),
    );
  const addItem = (ci: number) =>
    setCategories((prev) =>
      prev!.map((c, i) =>
        i === ci
          ? { ...c, items: [...c.items, { name: "", price: 0, halfPrice: 0, description: "", foodType: "veg" }] }
          : c,
      ),
    );

  const save = async () => {
    if (!categories) return;
    setSaving(true);
    try {
      const res = await api.post("/menu-import/apply", {
        categories,
        ...(targetId && { targetRestaurantId: targetId }),
      });
      const { createdItems, createdCategories } = res.data?.data || {};
      toast.success(
        `Added ${createdItems} items${createdCategories ? ` in ${createdCategories} new categories` : ""}` +
          (targetName ? ` to ${targetName}` : ""),
      );
      onImported();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Import failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={reading || saving ? undefined : onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="m-0 text-xl font-bold text-gray-800">Import menu from photo or PDF</h2>
          <button type="button" onClick={onClose} disabled={reading || saving} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {locations.length > 1 ? (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <label className="mb-1 flex items-center gap-1 text-sm font-semibold text-gray-800">
                <MapPin size={14} /> Add this menu to
              </label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                disabled={saving}
                className={inputClass}
              >
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.hotelName}
                    {loc._id === activeId ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            targetName && (
              <p className="m-0 mb-4 flex items-center gap-1 text-sm text-gray-600">
                <MapPin size={14} /> Adding to <b>{targetName}</b>
              </p>
            )
          )}
          {!categories ? (
            <div className="space-y-4">
              <p className="m-0 text-sm text-gray-600">
                Upload a clear photo or a PDF of your menu. AI reads the dishes and prices (handwritten
                price changes too, and every page of a PDF, up to {MAX_PDF_PAGES} pages) — you'll check the list before anything is added.
              </p>
              <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-300 p-6 text-blue-600 hover:bg-blue-50">
                {previewUrl ? (
                  <img src={previewUrl} alt="Menu" className="max-h-80 max-w-full rounded object-contain" />
                ) : file ? (
                  <span className="flex flex-col items-center gap-1 text-gray-700">
                    <FileText size={36} className="text-red-600" />
                    <span className="text-sm">{file.name}</span>
                  </span>
                ) : (
                  <Camera size={36} />
                )}
                <span className="text-sm font-semibold">
                  {file ? "Choose a different file" : "Choose menu photo or PDF"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => onSelect(e.target.files?.[0])}
                />
              </label>
              {reading && (
                <p className="m-0 flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Loader2 size={16} className="animate-spin" /> {progress || "Reading the menu — this can take a minute…"}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <p className="m-0 text-sm text-gray-600">
                Found <b>{itemCount}</b> items in <b>{categories.length}</b> categories. Check the names
                and prices, fix anything that was misread, then add them. For items with a Half plate, customers
                will choose Half or Full.
              </p>
              {categories.map((cat, ci) => (
                <div key={ci} className="rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 border-b bg-gray-50 px-3 py-2">
                    <input
                      value={cat.name}
                      onChange={(e) => updateCategory(ci, e.target.value)}
                      className={`${inputClass} font-semibold`}
                    />
                    <button type="button" onClick={() => removeCategory(ci)} className="text-red-600 hover:text-red-700" title="Remove category">
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Item</th>
                        <th className="w-28 px-2 py-2 font-medium">Half ({symbol})</th>
                        <th className="w-28 px-2 py-2 font-medium">Full / Price ({symbol})</th>
                        <th className="w-28 px-2 py-2 font-medium">Type</th>
                        <th className="w-10 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {cat.items.map((item, ii) => (
                        <tr key={ii} className="border-t">
                          <td className="px-3 py-1.5">
                            <input value={item.name} onChange={(e) => updateItem(ci, ii, { name: e.target.value })} className={inputClass} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min={0}
                              value={item.halfPrice || ""}
                              placeholder="—"
                              onChange={(e) => updateItem(ci, ii, { halfPrice: Number(e.target.value) || 0 })}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min={0}
                              value={item.price || ""}
                              onChange={(e) => updateItem(ci, ii, { price: Number(e.target.value) || 0 })}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={item.foodType}
                              onChange={(e) => updateItem(ci, ii, { foodType: e.target.value as ImportItem["foodType"] })}
                              className={inputClass}
                            >
                              <option value="veg">Veg</option>
                              <option value="non-veg">Non-veg</option>
                              <option value="both">Both</option>
                            </select>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button type="button" onClick={() => removeItem(ci, ii)} className="text-gray-400 hover:text-red-600" title="Remove item">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button type="button" onClick={() => addItem(ci)} className="m-2 flex items-center gap-1 text-sm text-blue-600 hover:underline">
                    <Plus size={14} /> Add item
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          {categories ? (
            <>
              <button type="button" onClick={() => setCategories(null)} disabled={saving} className="rounded-md bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300">
                Back
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !itemCount}
                className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Adding…" : `Add ${itemCount} items${targetName ? ` to ${targetName}` : " to menu"}`}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={readMenu}
              disabled={!file || reading}
              className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {reading ? "Reading…" : "Read menu"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuImportModal;
